"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import Zoom from "react-medium-image-zoom";

import type { Property } from "~/lib/types/payload";

import { S3Image } from "~/lib/components/s3-image";
import { VideoRenderer } from "~/lib/renderers/video";

import "react-medium-image-zoom/dist/styles.css";

import { AspectRatio } from "~/lib/shadcn/components/ui/aspect-ratio";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "~/lib/shadcn/components/ui/carousel";

export default function PropertiesShowCarousel({ media }: { media: Property["media"] }) {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);
	if (!media) return null;
	const slideMarkup = media.map((media, index) => {
		if (typeof media.asset.value === "number") return undefined;
		return (
			<CarouselItem
				className="basis-1/6"
				key={media.id}
				onClick={() => {
					api?.scrollTo(index);
					setCurrent(index);
				}}
			>
				<AspectRatio className="w-full bg-black text-white" ratio={4 / 3}>
					{media.asset.relationTo === "images" ? (
						media.asset.value.url ? (
							<S3Image
								image={media.asset.value}
								imageProps={{
									alt: media.asset.value.alternativeText,
									className: "object-cover w-full h-full",
									fill: true,
									sizes: "30vw",
									src: media.asset.value.url,
									style: {
										objectPosition: `${media.asset.value.focalX ?? 50}% ${media.asset.value.focalY ?? 50}%`,
									},
								}}
							/>
						) : null
					) : (
						<VideoRenderer
							aspectRatio={media.asset.value.aspectRatio}
							muxPlaybackId={media.asset.value.muxPlaybackId}
						/>
					)}
				</AspectRatio>
			</CarouselItem>
		);
	});
	return (
		<>
			<div className="relative">
				<AspectRatio className="w-full bg-black text-white" ratio={16 / 9}>
					{media[current].asset.relationTo === "images" &&
						typeof media[current].asset.value !== "number" &&
						media[current].asset.value.url && (
							<Zoom classDialog="bg-white">
								<S3Image
									image={media[current].asset.value}
									imageProps={{
										alt: media[current].asset.value.alternativeText,
										className: "object-cover w-full h-full",
										fill: true,
										sizes: "(max-width: 768px) 120vw, 60vw",
										src: media[current].asset.value.url,
										style: {
											objectPosition: `${media[current].asset.value.focalX ?? 50}% ${media[current].asset.value.focalY ?? 50}%`,
										},
									}}
								/>
							</Zoom>
						)}
					{media[current].asset.relationTo === "videos" && typeof media[current].asset.value !== "number" && (
						<VideoRenderer
							aspectRatio={media[current].asset.value.aspectRatio}
							muxPlaybackId={media[current].asset.value.muxPlaybackId}
						/>
					)}
				</AspectRatio>
				<button
					onClick={() => {
						let index = current - 1;
						if (index < 0) index = media.length - 1;
						api?.scrollTo(index);
						setCurrent(index);
					}}
				>
					<ChevronLeft className="z-10 absolute top-1/2 left-4 -translate-y-1/2 size-8 text-white" />
				</button>
				<button
					onClick={() => {
						const index = (current + 1) % media.length;
						api?.scrollTo(index);
						setCurrent(index);
					}}
				>
					<ChevronRight className="z-10 absolute top-1/2 right-4 -translate-y-1/2 size-8 text-white" />
				</button>
			</div>
			<Carousel className="mt-4" opts={{ loop: true }} setApi={setApi}>
				<CarouselContent>{slideMarkup}</CarouselContent>
			</Carousel>
		</>
	);
}
